import styled, { css } from 'styled-components';
import type { BackgroundColor } from '@amplience/content-types/typings/c-footer-newsletter-sign-up';
import { BlackButton, ErrorMessage, Fieldset } from '../../../baseComponents';
import { FlexibleTextPartial } from '../../../partials';
import { TypographyStyles, colours, maxWidthPartial, media, spacing } from '../../../stylings';

const multipleLanguagesStyles = css`
  display: grid;
  gap: ${spacing.XS} ${spacing.M};
  grid-template-columns: repeat(2, max-content);
  margin: 0;
  padding: 0;

  > li {
    margin: 0;
  }

  @media ${media.greaterThan('lg')} {
    display: flex;
    gap: ${spacing.XS};
    grid-template-columns: unset;
  }
`;

const singleLanguageStyles = css`
  display: grid;
  gap: 0 ${spacing.M};
  grid-template-columns: repeat(2, max-content);
  margin: 0;
  padding: 0;
`;

export const FullWidthContainer = styled.div<{ $bgColor?: BackgroundColor }>`
  background-color: ${({ $bgColor }) => ($bgColor ? $bgColor?.mobile : colours.LIGHT_BLUE)};

  @media ${media.greaterThan('lg')} {
    background-color: ${({ $bgColor }) => ($bgColor ? $bgColor?.desktop : colours.LIGHT_BLUE)};
  }
`;
export const NewsletterSignUpContainer = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  ${maxWidthPartial({ maxWidth: 1440, withPadding: false })}
  padding: ${spacing.L} ${spacing.S};

  @media ${media.greaterThan('lg')} {
    flex-direction: row;
    gap: 54px;
    justify-content: space-between;
    padding: 46px ${spacing.S} 48px ${spacing.S};
  }

  @media ${media.greaterThan('xl')} {
    padding: 46px 70px 48px;
  }
`;
export const FormContainer = styled.form`
  width: 100%;

  @media ${media.greaterThan('lg')} {
    display: flex;
    gap: 54px;
  }
`;
export const SelectionListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.XS};
  width: 100%;
`;
export const InputContainer = styled.div`
  width: 100%;

  > *:not(:last-child) {
    margin-bottom: ${spacing.XS};
  }

  @media ${media.greaterThan('lg')} {
    > *:not(:last-child) {
      margin-bottom: ${spacing.XXXS};
    }
  }
`;
export const Details = styled.div`
  margin-bottom: ${spacing.XS};

  > *:not(:last-child) {
    margin-bottom: ${spacing.XS};
  }

  @media ${media.greaterThan('lg')} {
    margin-bottom: 0;
    width: 479px;
  }
`;
export const Heading = styled(FlexibleTextPartial)`
  margin: 0;
  word-break: break-word;
`;
export const SubHeading = styled(FlexibleTextPartial)`
  margin: 0;
  word-break: break-word;
`;
export const ErrorStyled = styled(ErrorMessage)`
  margin-top: ${spacing.XXXXS};
`;
export const Input = styled.input`
  border: solid 1px ${colours.GREY};
  border-radius: 0;
  min-height: 48px;
  outline: none;
  padding: 0 ${spacing.XS};
  width: 100%;
  ${TypographyStyles.Body.Medium.Regular}

  &:focus {
    border: 2px solid ${colours.BLACK};
  }

  &::placeholder {
    color: ${colours.GREY};
  }

  @media ${media.greaterThan('lg')} {
    ${TypographyStyles.Body.Tiny.Regular}
  }

  @media ${media.greaterThan('xl')} {
    ${TypographyStyles.Body.Medium.Regular}
  }
`;
export const InputAndButton = styled.div<{ $scrollWidth: number }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.XS};

  @media ${media.greaterThan('xl')} {
    flex-direction: ${({ $scrollWidth }) => ($scrollWidth <= 430 ? 'column' : 'row')};
    gap: 5px;
    margin-bottom: ${spacing.XXXS};

    > * {
      width: 100%;
    }
  }
`;
export const SelectionList = styled(Fieldset)<{ $hasMultipleLanguages: boolean }>`
  ${({ $hasMultipleLanguages }) => ($hasMultipleLanguages ? multipleLanguagesStyles : singleLanguageStyles)}
  border: none;
`;
export const Selection = styled.div`
  align-items: center;
  display: flex;
  margin: 0 0 ${spacing.XS} 0;
  position: relative;
  width: max-content;

  > label {
    ${TypographyStyles.Body.Tiny.Regular}
    align-items: center;
    cursor: pointer;
    display: flex;
    height: 24px;
    overflow-wrap: anywhere;
    padding: 0 0 0 10px;
  }

  > input[type='checkbox'] {
    appearance: none;
    background-color: ${colours.WHITE};
    border: solid 1px ${colours.GREY};
    border-radius: 0;
    box-sizing: border-box;
    cursor: pointer;
    flex-shrink: 0;
    height: ${spacing.S};
    margin: 0;
    width: ${spacing.S};

    &:focus {
      outline-color: ${colours.OUTLINE_BORDER};
    }
  }

  > input[type='checkbox']:checked {
    background-color: ${colours.DARK_BLUE};
  }

  > svg {
    border-radius: 0px;
    display: inline-block;
    height: 15.1px;
    left: 6px;
    pointer-events: none;
    position: absolute;
    width: 15.1px;

    & path {
      fill: ${colours.WHITE};
    }
  }
`;
export const Button = styled(BlackButton)<{ $scrollWidth: number }>`
  border-radius: 0;
  cursor: pointer;
  height: 100%;
  margin: 0;
  min-height: 48px;
  overflow: hidden;
  overflow-wrap: anywhere;
  width: 100%;

  @media ${media.greaterThan('xl')} {
    max-width: ${({ $scrollWidth }) => ($scrollWidth <= 430 ? 'none' : '237px')};
  }
`;
